import BreadCrumb from "../../../components/BreadCrumb";
import Layout from "../../../components/Layout";
import { Grid } from "@mui/material";
import Button from "../../../components/Button";
import { toast } from "react-toastify";
import LessonNoteCard from "./LessonNoteCard";
import { Icon } from "@iconify/react";
import useStyles from "../styles";
import VerticalLine from "../../../assets/images/verticallines.svg";
// import Nosearch from "../../../assets/images/no-search.svg";
import { CardSearch } from "../../../components/CardSearch";
import { useNavigate, useParams } from "react-router-dom";
import { httprequest } from "../../../data/api";
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import Modal from "../../../components/Modal";
import HTMLDocx from "html-docx-js/dist/html-docx";
import { saveAs } from "file-saver";
import JSZip from "jszip";
// import NotFounditem from "../../error/NotFound";
import { useUserState } from "../../../context/UserContext";

const ALL_LESSON_NOTES = "/api/v1/lesson-notes";
const NOTE_API = "/api/v1/lesson-outlines";

const ViewLessonNote = () => {
  const classes = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();
  const [lessonNotes, setLessonNotes] = useState([]);
  const [lessonNoteDetails, setLessonNoteDetails] = useState([]);
  const [noteToDelete, setNoteToDelete] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const { userRole } = useUserState();
  const isTeacher = userRole?.role_type === "employee";
  const isAdmin = userRole?.role_type === "school_admin";

  // const location = useLocation();
  // const queryParams = new URLSearchParams(location.search);

  /**
 * The class ID from the lesson note details, used for identifying the class.
 * @type {string}
 */
  const class_id = lessonNoteDetails?.class?.id;

  /**
 * Sends a request to delete a lesson note by its outline ID.
 * On success, removes the note from the local state and shows a success toast.
 * Toggles the delete modal visibility.
 * Shows an error toast if the request fails.
 *
 * @param {string} lesson_outline_id - The ID of the lesson note to delete.
 */
  const deleteNote = async (lesson_outline_id) => {
    try {
      await httprequest.delete(`${NOTE_API}/${lesson_outline_id}`);
      toast.success("Lesson note deleted successfully");
      setLessonNotes((prevNotes) => prevNotes.filter((note) => note.lesson_outline_id !== lesson_outline_id));
      setIsDeleteModalOpen(!isDeleteModalOpen);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  /**
 * Opens or closes the delete modal for a specified note.
 *
 * @param {string} noteId - The ID of the note to be deleted.
 */
  const handleDeleteModal = (noteId) => {
    setNoteToDelete(noteId);
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  /**
 * Fetches all lesson notes for the current ID.
 * Updates the lesson note details, lesson notes list, search results, and timeline.
 * Manages the loading state and error notifications.
 */
  const fetchLessonNotes = () => {
    setIsLoading(true);
    httprequest
      .get(`${ALL_LESSON_NOTES}/${id}`)
      .then((res) => {
        setLessonNoteDetails(res?.data?.data);
        setLessonNotes(res?.data.data?.lesson_outlines);
        setSearchResult(res?.data.data?.lesson_outlines);
        setTimeline(res?.data?.data?.update_timeline);
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  useEffect(() => {
    fetchLessonNotes();
  }, []);

  /**
 * Navigates to the edit page for a given lesson note.
 *
 * @param {string} noteID - The ID of the lesson note to edit.
 */
  const editFunction = (noteID) => {
    navigate(`/lesson-notes/edit-lesson-note/${noteID}`);
  };

  /**
 * Handles search input changes and filters lesson notes by topic.
 * Matches all words typed in the search input against the topic of each note.
 *
 * @param {event parameter passed on handleChange} e - The input change event.
 */
  const handleNoteSearch = (e) => {
    const { value } = e.target;
    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = lessonNotes?.filter((data) => valArray?.every(
      (word) => data?.topic?.toLowerCase().includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  /**
   * Cleans HTML content for DOCX export while preserving math symbols.
   * Extracts readable text from KaTeX annotation tags and removes raw LaTeX
   * markup that would appear as gibberish in the exported document.
   */
  const cleanHtmlForExport = (html) => {
    if (!html) return "";
    let cleaned = html;

    // Extract readable text from KaTeX annotation elements (e.g. <annotation encoding="application/x-tex">x²+y²=z²</annotation>)
    cleaned = cleaned.replace(/<annotation[^>]*>([\s\S]*?)<\/annotation>/gi, (match, tex) => {
      return ` ${tex} `;
    });

    // Remove remaining KaTeX span wrappers but keep their text content
    cleaned = cleaned.replace(/<span class="katex[^"]*"[^>]*>([\s\S]*?)<\/span>/gi, "$1");

    // Remove KaTeX internal markup (mrow, msup, etc.) but keep text content
    cleaned = cleaned.replace(/<(?:mrow|msup|msub|mfrac|mi|mn|mo|mtext|math|semantics|mspace|mstyle|mover|munder)[^>]*>/gi, "");
    cleaned = cleaned.replace(/<\/(?:mrow|msup|msub|mfrac|mi|mn|mo|mtext|math|semantics|mspace|mstyle|mover|munder)>/gi, "");

    // Clean up any remaining raw LaTeX commands that weren't inside KaTeX elements
    cleaned = cleaned.replace(/\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\}/g, "");
    cleaned = cleaned.replace(/\\[a-zA-Z]+(\{[^}]*\})?(\{[^}]*\})?/g, "");
    cleaned = cleaned.replace(/\\\\/g, "");

    return cleaned;
  };

  /**
   * Sanitizes a filename by removing characters that are invalid in file systems.
   */
  const sanitizeFilename = (name) => {
    return name.replace(/[/\\?%*:|"<>]/g, "_").trim();
  };

  /**
   * Exports each lesson outline as a separate DOCX file, bundled inside a ZIP.
   * Preserves math symbols and calculations in the exported content.
   */
  const exportToDocx = async () => {
    if (!lessonNotes || lessonNotes.length === 0) {
      toast.error("No lesson outlines to export.");

      return;
    }

    const subject = lessonNoteDetails?.lesson_note?.subject?.name || lessonNoteDetails?.subject?.name || "Subject";
    const className = lessonNoteDetails?.lesson_note?.class?.name || lessonNoteDetails?.class?.name || "Class";
    const term = lessonNoteDetails?.lesson_note?.term?.name || lessonNoteDetails?.term?.name || "";

    try {
      const zip = new JSZip();

      lessonNotes.forEach((outline, idx) => {
        const topicName = outline?.topic || `Outline_${idx + 1}`;

        const html = `
          <html><head><meta charset="UTF-8"></head><body>
          <h1>${topicName}</h1>
          <p><strong>Subject:</strong> ${subject}</p>
          <p><strong>Class:</strong> ${className}</p>
          ${term ? `<p><strong>Term:</strong> ${term}</p>` : ""}
          <p><strong>Status:</strong> ${outline?.status || ""}</p>
          <p><strong>Training Method:</strong> ${outline?.training_method || ""}</p>
          <p><strong>Learning Goals:</strong> ${outline?.learning_goals || ""}</p>
          <p><strong>Learning Materials:</strong> ${outline?.learning_materials || ""}</p>
          <p><strong>Created by:</strong> ${outline?.created_by?.last_name || ""} ${outline?.created_by?.first_name || ""}</p>
          <p><strong>Created at:</strong> ${outline?.created_at?.substring(0, 10) || ""}</p>
          <hr/>
          <h2>Lesson Content</h2>
          ${cleanHtmlForExport(outline?.lesson_outline || "")}
          </body></html>
        `;

        const blob = HTMLDocx.asBlob(html);
        const fileName = `${sanitizeFilename(`${idx + 1}_${topicName}`)}.docx`;
        zip.file(fileName, blob);
      });

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${sanitizeFilename(subject)}_${sanitizeFilename(className)}_lesson_outlines.zip`);
      toast.success("Lesson outlines exported successfully.");
    } catch (error) {
      toast.error("Failed to export lesson outlines.");
    }
  };

  return (
    <Layout
      children={
        <>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/lesson-notes"
                link_one_name="Lesson Notes"
                active="View all Lesson Outlines"
                description={`View all the lesson outlines for ${lessonNoteDetails?.subject?.name}`}
              />
            </div>
            <div className={classes.titlebtn}>
              <Button
                variant="primaryoutline"
                buttonSize="fluid"
                color="btnfontprimary"
                onClick={() => navigate(`/lesson-notes/${id}/create-lesson-outline?subject_id=${lessonNoteDetails?.subject?.id}&class_id=${class_id}`)
                }
                buttonName="Create Lesson Outline"
              />
              <Button
                variant="primary"
                buttonSize="fluid"
                color="btndefault"
                onClick={() => navigate(`/lesson-notes/drafts/${id}`)}
                buttonName="View all Drafts"
              />
            </div>
          </div>
          <div>
            <div className="margin-bottom-10 margin-top-30">
              <CardSearch placeholder="Search Lesson Outline" onChange={handleNoteSearch} />
            </div>
            <Grid
              container
              spacing={4}
              sx={{
                "@media (max-width: 1000px)": {
                  flexWrap: "wrap-reverse"
                }
              }}
            >
              <Grid item lg={8} md={8} sm={12} xs={12}>
                <div className={`${classes.paddingcont}`}>
                  <div className="left-container">
                    <div>
                      {searchResult?.map((lesson, index) => {
                        return (
                          <LessonNoteCard
                            onClick={() => navigate(`/lesson-notes/lesson-outlines/${lesson?.lesson_outline_id}`)}
                            key={index}
                            title={lesson?.topic}
                            subtitle={parse(lesson?.lesson_outline)}
                            date={lesson?.created_at.substring(0, 10)}
                            status={lesson?.status}
                            viewRoute={`/lesson-notes/lesson-outlines/${lesson?.lesson_outline_id}`}
                            handleDelete={() => handleDeleteModal(lesson?.lesson_outline_id)}
                            editPath={{ pathname: `/lesson-notes/edit-lesson-outline/${lesson?.lesson_outline_id}` }}
                            editState={lesson}
                            handleEdit={() => editFunction(lesson?.lesson_outline_id)}
                            isTeacher={isTeacher}
                            isAdmin={isAdmin}
                            created_by={lesson?.created_by}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item lg={4} md={4} sm={12} xs={12}>
                <div className={`${classes.paddingcont}`}>
                  <div className={`${classes.rightcont}`}>
                    <div className={`${classes.notedetails}`}>
                      {timeline?.map((update, i) => {
                        return (
                          <div key={i}>
                            <div
                              className="flex gap-10 align-items-center margin-bottom-10"
                            >
                              <Icon
                                icon="lets-icons:check-fill"
                                width="32"
                                height="32"
                                style={{ color: "#3FAC64" }}
                              />
                              <div>
                                <h5>Updated at</h5>
                                <p>{update?.updated_at?.substring(0, 10)} | {update?.updated_at?.substring(11, 16)}</p>
                              </div>
                            </div>
                            <div className={classes.verticalline}>
                              <img src={VerticalLine} alt="" />
                            </div>
                          </div>
                        );
                      })}
                      {searchResult && (
                        <div className="flex gap-10 align-items-center margin-bottom-10">
                          <Icon
                            icon="mdi:cloud-clock-outline"
                            width="25"
                            height="25"
                            style={{
                              color: "#0e9504",
                              border: "1px solid #0e9504",
                              borderRadius: "20px",
                              padding: "5px",
                              marginLeft: "5px"
                            }}
                          />
                          <div>
                            <h5>Created at</h5>
                            <p>{searchResult?.[0]?.created_at?.substring(0, 10)} | {searchResult?.[0]?.created_at?.substring(11, 16)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={`${classes.exportbtn}`}>
                      <Button
                        variant="primaryoutline"
                        color="btnfontprimary"
                        buttonSize="full"
                        buttonName="Export All Lesson Outlines"
                        onClick={exportToDocx}
                      />
                    </div>
                  </div>
                </div>
              </Grid>
              {/* <ToastContainer /> */}
            </Grid>
          </div>
          <Modal
            title="Delete Lesson Outline"
            subtitle="Are you sure you want to permanently delete this lesson outline?"
            modalContent={<></>}
            modalFooter={
              <>
                <div className="flex gap-10">
                  <Button
                    variant="danger"
                    buttonName="Delete Lesson Outline"
                    buttonSize="full"
                    color="btndefault"
                    onClick={() => deleteNote(noteToDelete)}
                    isLoading={isLoading}
                  />
                </div>
              </>
            }
            isopen={isDeleteModalOpen}
            closeModal={handleDeleteModal}
          />
        </>
      }
    />
  );
};

export default ViewLessonNote;
